import React, { useState } from 'react';
import { X, FileText, Package, Wrench, CheckCircle2, DollarSign, Clock, Printer, ChevronDown, ChevronUp, Image } from 'lucide-react';
import { getAssetImageUrl } from '../utils/assetImageUrl';
import { AttachmentDisplay } from './ImageAttachment';

// Repair Summary Report Modal - comprehensive report for completed repairs
const RepairSummaryReport = ({ ticket, onClose, isDark }) => {
  const [isRepairOverviewExpanded, setIsRepairOverviewExpanded] = useState(false);
  const [isInspectionFindingsExpanded, setIsInspectionFindingsExpanded] = useState(false);
  const [isRepairDetailsExpanded, setIsRepairDetailsExpanded] = useState(false);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);
  const [isVendorInspectionImagesExpanded, setIsVendorInspectionImagesExpanded] = useState(false);
  const assetImageUrl = ticket.asset_image_url || 
    (ticket.asset_type ? getAssetImageUrl(ticket.asset_type + '.png') : null);

  // Calculate repair duration
  const getRepairDuration = () => {
    const startEntry = ticket.status_history?.find(h => h.status === 'In Repair');
    const endEntry = ticket.status_history?.find(h => h.status === 'Complete');
    
    if (!startEntry || !endEntry) return null;
    
    const start = new Date(startEntry.timestamp);
    const end = new Date(endEntry.timestamp);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Calculate total time from start to completion
  const getTotalDuration = () => {
    if (!ticket.created_at || !ticket.completed_at) return null;
    
    const start = new Date(ticket.created_at);
    const end = new Date(ticket.completed_at);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  // Parse inspection findings
  const getInspectionFindings = () => {
    if (!ticket.inspection_findings) return null;
    try {
      return typeof ticket.inspection_findings === 'string' 
        ? JSON.parse(ticket.inspection_findings) 
        : ticket.inspection_findings;
    } catch {
      return null;
    }
  };

  // Parse repair details - check both repair_details and damage_details
  const getRepairDetails = () => {
    // Check both possible field names
    const repairData = ticket.repair_details || ticket.damage_details;
    if (!repairData) return null;
    try {
      return typeof repairData === 'string' 
        ? JSON.parse(repairData) 
        : repairData;
    } catch {
      return null;
    }
  };

  const inspectionFindings = getInspectionFindings();
  const repairDetails = getRepairDetails();
  const repairDuration = getRepairDuration();
  const totalDuration = getTotalDuration();

  // Get checklist - either from checklist array or build from items
  const getChecklist = () => {
    if (repairDetails?.checklist && Array.isArray(repairDetails.checklist)) {
      return repairDetails.checklist;
    }
    // If no checklist but we have items, try to build checklist from items
    if (repairDetails?.items && Array.isArray(repairDetails.items)) {
      return repairDetails.items.map(item => ({
        id: item.id,
        label: item.label,
        status: item.cost > 0 ? 'Damaged' : 'Good',
        cost: item.cost || 0
      }));
    }
    return null;
  };

  const checklist = getChecklist();

  // Get vendor inspection images
  const getVendorInspectionImages = () => {
    if (repairDetails?.vendor_inspection_images && Array.isArray(repairDetails.vendor_inspection_images)) {
      return repairDetails.vendor_inspection_images;
    }
    return [];
  };

  const vendorInspectionImageIds = getVendorInspectionImages();

  // Debug: Log repair details structure
  console.log('Ticket data:', ticket);
  console.log('Repair Details:', repairDetails);
  console.log('Checklist:', checklist);
  console.log('Vendor Inspection Images:', vendorInspectionImageIds);
  console.log('Has damageDetails?', ticket.damage_details);
  console.log('Has repair_details?', ticket.repair_details);

  // Function to print/download report
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className={`border rounded-xl w-full max-w-4xl max-h-[90vh] shadow-2xl flex flex-col ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white border-gray-200'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-600/20' : 'bg-blue-50'}`}>
              <FileText size={20} className="text-blue-500" />
            </div>
            <div>
              <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Repair Summary Report
              </h2>
              <p className={`text-xs mt-0.5 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Complete repair workflow documentation
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
              title="Print Report"
            >
              <Printer size={18} />
            </button>
            <button 
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 hide-scrollbar">
          {/* Asset Information - NOT Collapsible */}
          <section className={`border rounded-lg p-4 ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
            <h3 className={`text-base font-bold mb-3 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Package size={18} className="text-blue-500" />
              Asset Information
            </h3>
            <div className="flex gap-4">
              {assetImageUrl && (
                <div className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-gray-300'}`}>
                  <img 
                    src={assetImageUrl} 
                    alt={ticket.asset_name || 'Asset'} 
                    className="w-full h-full object-contain p-2"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 flex-1">
                <div>
                  <p className={`text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Asset ID</p>
                  <p className={`text-sm font-semibold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{ticket.asset_id}</p>
                </div>
                <div>
                  <p className={`text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Asset Name</p>
                  <p className={`text-sm font-semibold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{ticket.asset_name || 'N/A'}</p>
                </div>
                <div>
                  <p className={`text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Asset Type</p>
                  <p className={`text-sm mt-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{ticket.asset_type || 'N/A'}</p>
                </div>
                <div>
                  <p className={`text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Priority</p>
                  <p className={`text-sm mt-1 ${
                    ticket.priority === 'High' || ticket.priority === 'Critical'
                      ? 'text-red-500 font-semibold'
                      : ticket.priority === 'Medium'
                      ? 'text-amber-500 font-semibold'
                      : isDark ? 'text-slate-300' : 'text-slate-700'
                  }`}>{ticket.priority || 'N/A'}</p>
                </div>
              </div>
            </div>
          </section>

          {/* Repair Overview - Collapsible */}
          <section className={`border rounded-lg ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
            <button
              onClick={() => setIsRepairOverviewExpanded(!isRepairOverviewExpanded)}
              className={`w-full flex items-center justify-between p-4 hover:opacity-80 transition-opacity ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-100'}`}
            >
              <div className="flex items-center gap-2">
                <Wrench size={18} className={isDark ? 'text-amber-500' : 'text-amber-600'} />
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Repair Overview</h3>
              </div>
              {isRepairOverviewExpanded ? (
                <ChevronUp size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              ) : (
                <ChevronDown size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              )}
            </button>
            
            {isRepairOverviewExpanded && (
              <div className={`px-4 pb-4 space-y-3 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className="grid grid-cols-3 gap-3">
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
                    <p className={`text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Repair Title</p>
                    <p className={`text-sm font-semibold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{ticket.title || 'N/A'}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
                    <p className={`text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Total Duration</p>
                    <p className={`text-lg font-bold mt-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      {totalDuration !== null ? `${totalDuration} days` : 'N/A'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
                    <p className={`text-xs font-semibold uppercase ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Repair Duration</p>
                    <p className={`text-lg font-bold mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                      {repairDuration !== null ? `${repairDuration} days` : 'N/A'}
                    </p>
                  </div>
                </div>
                {ticket.description && (
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
                    <p className={`text-xs font-semibold uppercase mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Description</p>
                    <p className={`text-sm italic ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>"{ticket.description}"</p>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Initial Inspection Findings - Collapsible */}
          {inspectionFindings && (
            <section className={`border rounded-lg ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
              <button
                onClick={() => setIsInspectionFindingsExpanded(!isInspectionFindingsExpanded)}
                className={`w-full flex items-center justify-between p-4 hover:opacity-80 transition-opacity ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-100'}`}
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={18} className={isDark ? 'text-red-500' : 'text-red-600'} />
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Initial Inspection Findings</h3>
                </div>
                {isInspectionFindingsExpanded ? (
                  <ChevronUp size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                ) : (
                  <ChevronDown size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                )}
              </button>
              
              {isInspectionFindingsExpanded && (
                <div className={`px-4 pb-4 space-y-3 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                  {inspectionFindings.checklist && (
                <div className="space-y-3">
                  <p className={`text-sm font-semibold mb-3 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Routine Inspection Checklist:</p>
                  {Object.entries(inspectionFindings.checklist).map(([key, value]) => {
                    const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                    const statusColor = value === 'good' 
                      ? 'text-emerald-500' 
                      : value === 'warning' 
                      ? 'text-amber-500' 
                      : 'text-red-500';
                    const bgColor = value === 'good'
                      ? isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'
                      : value === 'warning'
                      ? isDark ? 'bg-amber-500/10' : 'bg-amber-50'
                      : isDark ? 'bg-red-500/10' : 'bg-red-50';
                    
                    return (
                      <div key={key} className={`flex items-center justify-between p-3 rounded-lg ${bgColor}`}>
                        <span className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{label}</span>
                        <span className={`text-sm font-bold uppercase ${statusColor}`}>{value}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {inspectionFindings.notes && (
                <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
                  <p className={`text-xs font-semibold uppercase mb-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Inspector Notes</p>
                  <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{inspectionFindings.notes}</p>
                </div>
              )}

                  {ticket.inspection_images && ticket.inspection_images.length > 0 && (
                    <div>
                      <p className={`text-xs font-semibold uppercase mb-3 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Inspection Images</p>
                      <div className="grid grid-cols-4 gap-3">
                        {ticket.inspection_images.map((imageId, idx) => (
                          <AttachmentDisplay key={idx} fileId={imageId} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Repair Details & Cost Breakdown - Collapsible */}
          {(repairDetails || ticket.damage_details) && (
            <section className={`border rounded-lg ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
              <button
                onClick={() => setIsRepairDetailsExpanded(!isRepairDetailsExpanded)}
                className={`w-full flex items-center justify-between p-4 hover:opacity-80 transition-opacity ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-100'}`}
              >
                <div className="flex items-center gap-2">
                  <DollarSign size={18} className={isDark ? 'text-emerald-500' : 'text-emerald-600'} />
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Repair Details & Cost Breakdown</h3>
                </div>
                {isRepairDetailsExpanded ? (
                  <ChevronUp size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                ) : (
                  <ChevronDown size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                )}
              </button>
              
              {isRepairDetailsExpanded && (
                <div className={`px-4 pb-4 space-y-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                  {/* Repair Checklist */}
              {checklist && checklist.length > 0 && (
                <div className="mb-4">
                  <p className={`text-sm font-semibold mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Repair Checklist:</p>
                  <div className="space-y-2">
                    {checklist.map((item, idx) => {
                      const isDamaged = item.status === 'Damaged';
                      const bgColor = isDamaged
                        ? isDark ? 'bg-red-500/10 border-red-500/30' : 'bg-red-50 border-red-200'
                        : isDark ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200';
                      const textColor = isDamaged
                        ? isDark ? 'text-red-400' : 'text-red-700'
                        : isDark ? 'text-emerald-400' : 'text-emerald-700';
                      
                      return (
                        <div key={idx} className={`flex items-center justify-between p-2 rounded-lg border ${bgColor}`}>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>{item.label}</span>
                            <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${textColor} ${isDark ? 'bg-slate-800/50' : 'bg-white/50'}`}>
                              {item.status}
                            </span>
                          </div>
                          {isDamaged && (
                            <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              ${parseFloat(item.cost || 0).toFixed(2)}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Total Cost from Checklist */}
                  {(() => {
                    const checklistTotal = checklist
                      .filter(item => item.status === 'Damaged')
                      .reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
                    
                    if (checklistTotal > 0) {
                      return (
                        <div className={`mt-3 pt-3 border-t ${isDark ? 'border-slate-700' : 'border-gray-300'}`}>
                          <div className="flex items-center justify-between">
                            <span className={`text-sm font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Total Cost (from checklist):</span>
                            <span className={`text-lg font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                              ${checklistTotal.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })()}
                </div>
              )}

              {/* Cost Breakdown Table */}
              {repairDetails.items && repairDetails.items.length > 0 && (
                <div className={`rounded-lg overflow-hidden border ${isDark ? 'border-slate-700' : 'border-gray-300'}`}>
                  <table className="w-full">
                    <thead className={`${isDark ? 'bg-slate-900/80' : 'bg-gray-100'}`}>
                      <tr>
                        <th className={`text-left p-3 text-xs font-semibold uppercase ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Item</th>
                        <th className={`text-right p-3 text-xs font-semibold uppercase ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Cost</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDark ? 'bg-slate-900/50' : 'bg-white'} divide-y ${isDark ? 'divide-slate-700' : 'divide-gray-200'}`}>
                      {repairDetails.items.map((item, idx) => (
                        <tr key={idx}>
                          <td className={`p-3 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item.label}</td>
                          <td className={`p-3 text-sm text-right font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                            ${parseFloat(item.cost || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      <tr className={`${isDark ? 'bg-slate-800' : 'bg-gray-50'} font-bold`}>
                        <td className={`p-3 text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>Total Cost</td>
                        <td className={`p-3 text-lg text-right ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          ${parseFloat(ticket.total_repair_cost || 0).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {repairDetails.notes && (
                <div className={`mt-4 p-4 rounded-lg ${isDark ? 'bg-slate-900/50' : 'bg-white'}`}>
                  <p className={`text-xs font-semibold uppercase mb-2 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Vendor Notes</p>
                  <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{repairDetails.notes}</p>
                </div>
              )}

                  {ticket.repair_images && ticket.repair_images.length > 0 && (
                    <div>
                      <p className={`text-xs font-semibold uppercase mb-3 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Repair Images</p>
                      <div className="grid grid-cols-4 gap-3">
                        {ticket.repair_images.map((imageId, idx) => (
                          <AttachmentDisplay key={idx} fileId={imageId} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Vendor Inspection Images - Collapsible */}
          {vendorInspectionImageIds && vendorInspectionImageIds.length > 0 && (
            <section className={`border rounded-lg ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
              <button
                onClick={() => setIsVendorInspectionImagesExpanded(!isVendorInspectionImagesExpanded)}
                className={`w-full flex items-center justify-between p-4 hover:opacity-80 transition-opacity ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-100'}`}
              >
                <div className="flex items-center gap-2">
                  <Image size={18} className={isDark ? 'text-purple-500' : 'text-purple-600'} />
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Vendor Inspection Images
                  </h3>
                  <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-gray-200 text-gray-600'}`}>
                    {vendorInspectionImageIds.length}
                  </span>
                </div>
                {isVendorInspectionImagesExpanded ? (
                  <ChevronUp size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                ) : (
                  <ChevronDown size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
                )}
              </button>
              
              {isVendorInspectionImagesExpanded && (
                <div className={`px-4 pb-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                  <p className={`text-sm mb-3 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                    Images uploaded by vendor during inspection
                  </p>
                  <div className="grid grid-cols-4 gap-3">
                    {vendorInspectionImageIds.map((imageId, idx) => (
                      <AttachmentDisplay key={idx} fileId={imageId} />
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}

          {/* Timeline - Collapsible */}
          <section className={`border rounded-lg ${isDark ? 'bg-slate-900/50 border-slate-700' : 'bg-gray-50 border-gray-200'}`}>
            <button
              onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
              className={`w-full flex items-center justify-between p-4 hover:opacity-80 transition-opacity ${isDark ? 'hover:bg-slate-800/50' : 'hover:bg-gray-100'}`}
            >
              <div className="flex items-center gap-2">
                <Clock size={18} className={isDark ? 'text-blue-500' : 'text-blue-600'} />
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Timeline</h3>
              </div>
              {isTimelineExpanded ? (
                <ChevronUp size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              ) : (
                <ChevronDown size={20} className={isDark ? 'text-slate-400' : 'text-gray-500'} />
              )}
            </button>
            
            {isTimelineExpanded && (
              <div className={`px-4 pb-4 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className={`relative border-l-2 ml-3 space-y-6 pt-4 ${isDark ? 'border-slate-700' : 'border-gray-300'}`}>
              {/* Repair Created */}
              {ticket.created_at && (
                <div className="relative pl-6">
                  <div className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-2 ${isDark ? 'bg-blue-500 border-slate-900' : 'bg-blue-500 border-white'}`}></div>
                  <div>
                    <p className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {new Date(ticket.created_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                    <p className={`text-sm font-semibold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Repair Request Created</p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Initial inspection failed, repair workflow initiated</p>
                  </div>
                </div>
              )}

              {/* Sent to Vendor */}
              {ticket.sent_to_vendor_at && (
                <div className="relative pl-6">
                  <div className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-2 ${isDark ? 'bg-purple-500 border-slate-900' : 'bg-purple-500 border-white'}`}></div>
                  <div>
                    <p className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {new Date(ticket.sent_to_vendor_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                    <p className={`text-sm font-semibold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Sent to Vendor</p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Asset sent to {ticket.vendor_name || 'vendor'} for inspection</p>
                  </div>
                </div>
              )}

              {/* Needs Approval */}
              {ticket.needs_approval_at && (
                <div className="relative pl-6">
                  <div className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-2 ${isDark ? 'bg-amber-500 border-slate-900' : 'bg-amber-500 border-white'}`}></div>
                  <div>
                    <p className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {new Date(ticket.needs_approval_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                    <p className={`text-sm font-semibold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Vendor Inspection Complete</p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Awaiting customer approval for ${parseFloat(ticket.total_repair_cost || 0).toFixed(2)}</p>
                  </div>
                </div>
              )}

              {/* In Repair */}
              {ticket.in_repair_at && (
                <div className="relative pl-6">
                  <div className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-2 ${isDark ? 'bg-blue-500 border-slate-900' : 'bg-blue-500 border-white'}`}></div>
                  <div>
                    <p className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {new Date(ticket.in_repair_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                    <p className={`text-sm font-semibold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Repair Approved & Started</p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Customer approved estimate, vendor began repairs</p>
                  </div>
                </div>
              )}

              {/* Complete */}
              {ticket.completed_at && (
                <div className="relative pl-6">
                  <div className={`absolute -left-[9px] top-1.5 w-4 h-4 rounded-full border-2 ${isDark ? 'bg-emerald-500 border-slate-900' : 'bg-emerald-500 border-white'}`}></div>
                  <div>
                    <p className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                      {new Date(ticket.completed_at).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit'
                      })}
                    </p>
                    <p className={`text-sm font-semibold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>Repair Complete</p>
                    <p className={`text-xs mt-1 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>Asset repaired and ready for return to service</p>
                  </div>
                </div>
              )}
                </div>
              </div>
            )}
          </section>

          {/* Summary Footer */}
          <section className={`border-2 rounded-lg p-4 ${isDark ? 'bg-emerald-900/20 border-emerald-800/50' : 'bg-emerald-50 border-emerald-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>Repair Successfully Completed</p>
                <p className={`text-xs mt-1 ${isDark ? 'text-emerald-300/70' : 'text-emerald-600'}`}>
                  Report generated on {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>
              <CheckCircle2 size={32} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
            </div>
          </section>
        </div>

        {/* Footer Actions */}
        <div className={`p-6 border-t flex justify-end gap-3 shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={handlePrint}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              isDark
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <Printer size={16} />
            Print Report
          </button>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              isDark
                ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RepairSummaryReport;
